import { ref, get, update, onValue } from 'firebase/database';
import { auth } from '../firebase';
import { SLOT_STATUS_CLOSED } from '../constants/bookingConst';
import { hasReceipt } from './receiptUtil';

/**
 * Whether the admin has closed one specific (timeSlot, court) cell on the
 * "Manage availability" grid — the per-hour equivalent of switching a court
 * off in Config -> Sports & Courts, and blocked for guests in exactly the
 * same way.
 *
 * Kept as a helper rather than an inline `=== 'closed'` because three
 * screens ask the same question (the guest grid, the checkout pre-check and
 * the admin grid's own styling) and a stale override map arrives as `{}`,
 * `null` or undefined depending on which of them is asking.
 *
 * @param {Object} slotOverrides - A { [timeSlot]: { [court]: status } } map,
 *   as read from slotOverrides/{date}. Missing/empty is treated as "nothing
 *   is closed".
 * @param {string} timeSlot - The slot key, e.g. "6-7AM".
 * @param {number|string} court - The court number.
 * @returns {boolean} True when this cell is explicitly closed.
 */
export const isSlotClosed = (slotOverrides, timeSlot, court) =>
  slotOverrides?.[timeSlot]?.[court] === SLOT_STATUS_CLOSED;

/**
 * A booking never blocks its slot once cancelled/disapproved. An
 * 'awaiting_payment' booking whose payment window has lapsed WITHOUT a
 * receipt is treated as expired too, even before anything has actually
 * written 'cancelled' to it — this is what actually frees the slot for
 * other guests in real time.
 *
 * Status lifecycle: 'awaiting_payment' (just reserved, guest still has the
 * countdown to pay and upload a receipt) -> 'pending' (receipt attached —
 * now actually awaiting admin review; "Payment Done" just moves the guest
 * on to the confirmation screen) -> 'confirmed' / 'disapproved' (admin
 * decision), or 'cancelled' at any point.
 *
 * @param {Object} data - A raw booking record from Firebase.
 * @returns {boolean} Whether this booking should still count as holding its slot.
 */
export const isBookingActive = (data) => {
  if (!data) return false;
  if (data.status === 'cancelled' || data.status === 'disapproved') return false;
  // hasReceipt(), not `data.paymentScreenshotUrl`: receipts now live in their
  // own node and the record only carries a flag (see receiptUtil.js). Reading
  // the old field alone would treat a paid-but-not-yet-backfilled booking as
  // unpaid and, past its deadline, quietly release the slots it is holding.
  if (data.status === 'awaiting_payment' && !hasReceipt(data) && data.paymentDeadline) {
    if (new Date(data.paymentDeadline).getTime() < Date.now()) return false;
  }
  return true;
};

/**
 * Builds the { timeSlot: [{ court, userName, status }] } map the booking
 * screens render from, out of a Firebase snapshot of `bookings` already
 * filtered to a single date. Only actively-held slots (see isBookingActive)
 * are included.
 *
 * @param {Object} snapshot - A Firebase DataSnapshot from a `bookings`
 *   query filtered to the date in question.
 * @returns {Object} The bookedSlots map.
 */
export const buildBookedSlotsFromSnapshot = (snapshot) => {
  const slots = {};
  if (snapshot && snapshot.exists()) {
    snapshot.forEach((childSnapshot) => {
      const data = childSnapshot.val();
      if (isBookingActive(data) && data.timeSlots) {
        Object.entries(data.timeSlots).forEach(([timeSlot, courts]) => {
          const courtList = Array.isArray(courts) ? courts : Object.keys(courts).map(Number);
          courtList.forEach(court => {
            if (!slots[timeSlot]) slots[timeSlot] = [];
            slots[timeSlot].push({
              court,
              userName: data.userName || 'Unknown',
              status: data.status || 'pending',
              // Lets the guest grid tell a club-held hour from a guest's
              // booking without matching on the booker's name, which a real
              // guest could in principle share.
              adminBlock: data.adminBlock === true,
            });
          });
        });
      }
    });
  }
  return slots;
};

// A slotLocks entry is { bookingId, uid, expiresAt }, expiresAt in EPOCH MS
// on the SERVER's clock (see getServerTimeOffset). `uid` is the Firebase user
// who wrote it, so the database rules can tell a visitor's own claim apart
// from someone else's.
//
// expiresAt mirrors the booking's short payment-countdown deadline while it's
// still 'awaiting_payment'; promoteBookingSlots() clears it once the booking
// is actually submitted (receipt uploaded / 'pending'), so a real reservation
// stays held indefinitely rather than freeing itself up. A lock with no
// expiresAt never expires on its own — it only comes off via
// releaseBookingSlots() when the booking is cancelled/declined.
//
// WHY EVERY WRITE HERE TARGETS SINGLE LOCKS (slotLocks/{date}/{slot}/{court})
// instead of transacting on the whole day's map: database.rules.json grants
// write permission per lock. That is what lets it cap a guest's expiresAt at
// the configured payment timeout and stop one visitor deleting another's
// claim. A write to slotLocks/{date} would sit above those rules and skip
// them — and Firebase never evaluates .validate on a delete, so a whole-map
// rewrite could drop any lock it liked. The double-booking guarantee is kept:
// a multi-path update() is all-or-nothing, and the per-lock rule refuses to
// overwrite a live claim held by another booking, so two guests racing for
// one slot still cannot both win.

/**
 * Whether a lock is no longer holding its slot.
 *
 * @param {object|null} lock - { bookingId, uid, expiresAt }.
 * @param {number} nowMs - the current time on the server's clock.
 * @returns {boolean}
 */
const isLockExpired = (lock, nowMs) => {
  if (!lock) return true;
  if (!lock.expiresAt) return false;
  return new Date(lock.expiresAt).getTime() < nowMs;
};

/**
 * Normalises a claim deadline to EPOCH MILLISECONDS.
 *
 * The database rule that lets a claimer take over an expired lock compares
 * `expiresAt` with `now`, and `now` is epoch ms. This used to be stored as an
 * ISO string, which made that a string-vs-number comparison — never true, so
 * the take-over branch was dead and an abandoned lock became permanent.
 *
 * Done HERE rather than at the call sites so every caller is covered and
 * `paymentDeadline` can stay an ISO string on the booking record itself,
 * where the countdown UI already reads it.
 *
 * @param {string|number|null|undefined} value - ISO string, epoch ms, or null.
 * @returns {number|null} epoch ms, or null for "never expires".
 */
const toExpiryMs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Reads how far this device's clock is from Firebase's server clock.
 *
 * The database rule caps a guest's expiresAt at the server's `now` plus the
 * payment timeout. A phone clock running a few minutes fast would otherwise
 * produce a deadline past that cap and have the whole claim refused, so
 * deadlines are shifted onto the server clock before they are written.
 *
 * @param {Object} db - The Firebase Realtime Database instance.
 * @returns {Promise<number>} milliseconds to add to Date.now(); 0 if unknown.
 */
const getServerTimeOffset = (db) => new Promise((resolve) => {
  onValue(ref(db, '.info/serverTimeOffset'), (snapshot) => {
    const offset = Number(snapshot.val());
    resolve(Number.isFinite(offset) ? offset : 0);
  }, () => resolve(0), { onlyOnce: true });
});

/**
 * Reads one day's lock map.
 *
 * @param {Object} db - The Firebase Realtime Database instance.
 * @param {string} dateStr - "yyyy-MM-dd".
 * @returns {Promise<Object>} { timeSlot: { court: lock } }, or {} when empty.
 */
const readDateLocks = async (db, dateStr) => {
  const snapshot = await get(ref(db, `slotLocks/${dateStr}`));
  return snapshot.val() || {};
};

/**
 * Lists the requested slots that another booking still holds.
 *
 * @param {Object} locks - one day's lock map (see readDateLocks).
 * @param {Object} selectedSlots - { timeSlot: [court, ...] } being claimed.
 * @param {string} bookingId - the booking doing the claiming.
 * @param {number} nowMs - the current time on the server's clock.
 * @returns {Array<{timeSlot: string, court: number, bookedBy: string}>}
 */
const findLockConflicts = (locks, selectedSlots, bookingId, nowMs) => {
  const conflicts = [];
  Object.entries(selectedSlots).forEach(([timeSlot, courts]) => {
    (courts || []).forEach((court) => {
      const existing = locks[timeSlot]?.[court];
      if (existing && existing.bookingId !== bookingId && !isLockExpired(existing, nowMs)) {
        conflicts.push({ timeSlot, court, bookedBy: existing.bookingId });
      }
    });
  });
  return conflicts;
};

/**
 * Whether a Firebase error is a security-rule rejection.
 *
 * @param {Error} error
 * @returns {boolean}
 */
const isPermissionDenied = (error) => /permission[_ ]denied/i.test(`${error?.code || ''} ${error?.message || ''}`);

/**
 * Deletes every lock on `dateStr` that `shouldDrop` selects, in one
 * all-or-nothing multi-path update of single-lock paths (see the note at the
 * top of this section for why).
 *
 * @param {Object} db - The Firebase Realtime Database instance.
 * @param {string} dateStr - "yyyy-MM-dd".
 * @param {(lock: {timeSlot: string, court: string, bookingId: string, expiresAt: *}) => boolean} shouldDrop
 *   returns true for each lock to delete.
 * @returns {Promise<number>} how many locks were removed.
 */
export const removeSlotLocksWhere = async (db, dateStr, shouldDrop) => {
  if (!db || !dateStr) return 0;
  const locks = await readDateLocks(db, dateStr);
  const updates = {};
  Object.entries(locks).forEach(([timeSlot, courts]) => {
    Object.entries(courts || {}).forEach(([court, lock]) => {
      if (lock && shouldDrop({ timeSlot, court, bookingId: lock.bookingId, expiresAt: lock.expiresAt })) {
        updates[`slotLocks/${dateStr}/${timeSlot}/${court}`] = null;
      }
    });
  });
  const count = Object.keys(updates).length;
  if (count > 0) await update(ref(db), updates);
  return count;
};

/**
 * Atomically claims slots on one or more dates, optionally writing other
 * records in the SAME update.
 *
 * Everything goes out as one multi-path update(): it is all-or-nothing, and
 * the per-lock database rule refuses to overwrite another booking's live
 * claim. So if a competing claim lands between the read below and this
 * write, the whole write is rejected and nothing is partially claimed —
 * this is what closes the double-booking race window.
 *
 * `extraWrites` exists for bookings that are confirmed the moment they are
 * created (admin walk-ins and club holds). Writing the 'confirmed' record in
 * the same update as its locks is what lets those locks carry no expiry: the
 * rule allows an open-ended lock only when the booking it names is
 * 'pending'/'confirmed' in the data as it will be after the write.
 *
 * @param {Object} db - The Firebase Realtime Database instance.
 * @param {Array<{dateStr: string, timeSlots: Object, bookingId: string}>} claims
 *   - one entry per date; timeSlots is { timeSlot: [court, ...] }.
 * @param {string|number|null} expiresAt - claim deadline (ISO or epoch ms),
 *   or null for an open-ended hold (refused unless the booking is
 *   'pending'/'confirmed' after this write).
 * @param {Object} [extraWrites={}] - { path: value } written in the same update.
 * @returns {Promise<{granted: boolean, conflicts: Array<{dateStr: string, timeSlot: string, court: number, bookedBy: string}>}>}
 * @throws {Error} when the write is refused for a reason other than a taken slot.
 */
export const claimSlotsAtomically = async (db, claims, expiresAt = null, extraWrites = {}) => {
  if (!db || !claims?.length) return { granted: false, conflicts: [] };
  const offset = await getServerTimeOffset(db);
  const clientExpiry = toExpiryMs(expiresAt);
  const expiresAtMs = clientExpiry === null ? null : clientExpiry + offset;

  // Re-run after a refused write too, so the caller sees exactly which
  // slot(s) beat it on the server, not a guess from before the race.
  const findAllConflicts = async () => {
    const all = [];
    for (const { dateStr, timeSlots, bookingId } of claims) {
      // eslint-disable-next-line no-await-in-loop
      const locks = await readDateLocks(db, dateStr);
      findLockConflicts(locks, timeSlots, bookingId, Date.now() + offset)
        .forEach((conflict) => all.push({ dateStr, ...conflict }));
    }
    return all;
  };

  const conflicts = await findAllConflicts();
  if (conflicts.length > 0) return { granted: false, conflicts };

  const uid = auth?.currentUser?.uid || '';
  const updates = { ...extraWrites };
  claims.forEach(({ dateStr, timeSlots, bookingId }) => {
    Object.entries(timeSlots).forEach(([timeSlot, courts]) => {
      (courts || []).forEach((court) => {
        updates[`slotLocks/${dateStr}/${timeSlot}/${court}`] = { bookingId, uid, expiresAt: expiresAtMs };
      });
    });
  });

  try {
    await update(ref(db), updates);
    return { granted: true, conflicts: [] };
  } catch (error) {
    if (!isPermissionDenied(error)) throw error;
    const lost = await findAllConflicts();
    // Nothing taken means the rules refused for another reason (a deadline
    // past the payment-timeout cap, a lost session). Reporting that as "slot
    // taken" would send the user hunting for a slot that is actually free.
    if (lost.length === 0) throw error;
    return { granted: false, conflicts: lost };
  }
};

/**
 * Claims the slots of one booking on one date — see claimSlotsAtomically.
 * Used by the guest checkout (with a payment-window deadline) and by the
 * late-receipt re-claim on the booking status screen.
 *
 * @param {Object} db - The Firebase Realtime Database instance.
 * @param {string} dateStr - The booking date, "yyyy-MM-dd".
 * @param {Object} selectedSlots - { timeSlot: [court, ...] } being claimed.
 * @param {string} bookingId - The (already-generated) key of the booking
 *   record these slots are being claimed for.
 * @param {string|number|null} expiresAt - claim deadline, or null for an
 *   open-ended hold (only for a booking already 'pending'/'confirmed').
 * @returns {Promise<{granted: boolean, conflicts: Array<{dateStr: string, timeSlot: string, court: number, bookedBy: string}>}>}
 */
export const claimBookingSlots = (db, dateStr, selectedSlots, bookingId, expiresAt = null) =>
  claimSlotsAtomically(db, [{ dateStr, timeSlots: selectedSlots, bookingId }], expiresAt);

/**
 * Releases every slotLocks entry owned by `bookingId` on `dateStr`. Call
 * this whenever a booking stops actively holding its slot — the guest
 * backs out or lets the payment window lapse, or an admin declines/cancels
 * it — so the slot becomes claimable again immediately instead of waiting
 * for its expiresAt (bookings with no expiresAt, e.g. anything already
 * 'pending'/'confirmed', would otherwise never free up on their own).
 *
 * The rules allow this only for the visitor who made the claim, an admin,
 * or anyone once the claim has expired.
 *
 * @param {Object} db - The Firebase Realtime Database instance.
 * @param {string} dateStr - The booking's date, "yyyy-MM-dd".
 * @param {string} bookingId - The booking whose claims should be released.
 * @returns {Promise<void>}
 */
export const releaseBookingSlots = async (db, dateStr, bookingId) => {
  if (!db || !dateStr || !bookingId) return;
  await removeSlotLocksWhere(db, dateStr, (lock) => lock.bookingId === bookingId);
};

/**
 * Clears a booking's slotLocks expiry once it moves past the short
 * payment-countdown window (receipt uploaded, now 'pending' admin review).
 * From that point the slot should stay held indefinitely, not free itself
 * up mid-review just because the original countdown ran out.
 *
 * Must run AFTER the booking's status is written: the rules allow an
 * open-ended claim only for a booking that is already 'pending' or
 * 'confirmed' — admins included.
 *
 * @param {Object} db - The Firebase Realtime Database instance.
 * @param {string} dateStr - The booking's date, "yyyy-MM-dd".
 * @param {string} bookingId - The booking whose claims should be promoted.
 * @returns {Promise<void>}
 */
export const promoteBookingSlots = async (db, dateStr, bookingId) => {
  if (!db || !dateStr || !bookingId) return;
  const locks = await readDateLocks(db, dateStr);
  const uid = auth?.currentUser?.uid || '';
  const updates = {};
  Object.entries(locks).forEach(([timeSlot, courts]) => {
    Object.entries(courts || {}).forEach(([court, lock]) => {
      if (lock?.bookingId === bookingId) {
        // No expiresAt — held until released.
        updates[`slotLocks/${dateStr}/${timeSlot}/${court}`] = { bookingId, uid };
      }
    });
  });
  if (Object.keys(updates).length > 0) await update(ref(db), updates);
};
